'use client';

import { c as _c } from "react/compiler-runtime";
import { m, MotionConfig, Transition } from 'motion/react';
import React, { useEffectEvent } from 'react';
import Image from 'next/image';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useWebHaptics } from 'web-haptics/react';
import { TIMING, CD_CONFIG } from './SpinningCD.config';
interface SpinningCDProps {
  artist?: string;
  title?: string;
  albumArt?: string;
  /**
   * optional explicit size (width/height in px) for the component. if omitted it will
   * choose 400 on desktop & 280 on mobile.
   */
  size?: number;

  /**
   * set true when you want to render the cd inline (e.g., inside a list item)
   * this removes the full-screen centering wrapper.
   */
  inline?: boolean;

  /**
   * Spotify preview URL (30-second clip) - plays on hover (desktop) or tap (mobile)
   */
  previewUrl?: string | null;

  /**
   * Fired the moment a preview actually starts playing (hover on desktop, tap on
   * mobile). Lets callers record engagement without coupling this UI primitive to
   * any analytics layer.
   */
  onPreviewPlay?: () => void;
}
type CDStatus = 'idle' | 'expanded';
const Context = React.createContext<{
  status: CDStatus;
  setStatus: React.Dispatch<React.SetStateAction<CDStatus>>;
}>({
  status: 'idle',
  setStatus: () => null
});

/* ─────────────────────────────────────────────────────────
 * SPINNING CD — interaction storyboard
 *
 * Interaction-driven (no timeline): a single `status` drives it.
 *
 *  idle             record spins linearly — 5s/rev, 2.5s/rev while audio plays;
 *                   title + artist shown below
 *  hover (desktop)  preview audio fades in; CD scales to 1.08
 *  click            toggles status:
 *    → expanded     spin stops, CD scales 1.12 + corners square off,
 *                   rings + title/artist fade out (info blurs, scales 0.95)
 *    → idle         reverses
 *  Esc              collapse + stop audio
 * ───────────────────────────────────────────────────────── */

/* Shared settle spring (also the MotionConfig default for the subtree). */
const SPRING_CD: Transition = {
  type: 'spring',
  bounce: 0,
  duration: 0.4
};

/* TIMING + CD_CONFIG live in ./SpinningCD.config so this component file only
   exports a component (Fast Refresh) while /dev/motion can still tune them. */

/* Title/artist block under the disc */
const INFO = {
  restingScale: 1,
  interactingScale: 0.95,
  // shrinks slightly while interacting
  blurPx: 4 // blur applied while interacting
};

/* Preview audio fade */
const AUDIO = {
  maxVolume: 0.5,
  fadeStep: 0.05,
  // volume delta per tick
  fadeIntervalMs: 30
};
function CD(t0) {
  const $ = _c(34);
  const {
    albumArt,
    title,
    size,
    isAudioPlaying: t1
  } = t0;
  const isAudioPlaying = t1 === undefined ? false : t1;
  const ctx = React.use(Context);
  const haptic = useWebHaptics();
  const isExpanded = ctx.status === "expanded";
  const spinDuration = isAudioPlaying ? TIMING.spinPlaying : TIMING.spinIdle;
  const radius = size / 2;
  const yOffset = size < CD_CONFIG.smallSize ? 0 : -(radius - CD_CONFIG.edgeInset);
  let t2;
  if ($[0] !== radius || $[1] !== yOffset) {
    t2 = {
      rotate: 0,
      x: "-50%",
      y: yOffset,
      borderRadius: radius
    };
    $[0] = radius;
    $[1] = yOffset;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isExpanded || $[4] !== radius || $[5] !== spinDuration || $[6] !== yOffset) {
    t3 = isExpanded ? {
      rotate: 0,
      scale: CD_CONFIG.expandedScale,
      y: 0,
      borderRadius: 0,
      transition: {
        ...SPRING_CD,
        duration: TIMING.morph
      }
    } : {
      rotate: 360,
      scale: 1,
      y: yOffset,
      borderRadius: radius,
      transition: {
        rotate: {
          ease: "linear",
          duration: spinDuration,
          repeat: Infinity
        },
        default: {
          ...SPRING_CD,
          duration: TIMING.morph
        }
      }
    };
    $[3] = isExpanded;
    $[4] = radius;
    $[5] = spinDuration;
    $[6] = yOffset;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = isExpanded ? CD_CONFIG.hoverScaleExpanded : CD_CONFIG.hoverScaleIdle;
  let t5;
  if ($[8] !== t4) {
    t5 = {
      scale: t4
    };
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== ctx || $[11] !== haptic) {
    t6 = () => {
      haptic.trigger("medium");
      ctx.setStatus(_temp);
    };
    $[10] = ctx;
    $[11] = haptic;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== size) {
    t7 = {
      width: size,
      height: size
    };
    $[13] = size;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== albumArt || $[16] !== title) {
    t8 = albumArt ? <Image src={albumArt} alt={title} fill={true} sizes="(max-width: 768px) 280px, 400px" className="pointer-events-none object-cover select-none" /> : <div className="absolute inset-0 bg-gradient-to-br from-zinc-700 to-zinc-900" />;
    $[15] = albumArt;
    $[16] = title;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== isExpanded) {
    t9 = isExpanded ? {
      opacity: 0
    } : {
      opacity: 1
    };
    $[18] = isExpanded;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [{
      ratio: 0.3,
      className: "bg-neutral-500/20 backdrop-blur-sm"
    }, {
      ratio: 0.286,
      className: "border-[5px] border-dotted border-neutral-200/10"
    }, {
      ratio: 0.254,
      className: "border-[1.5px] border-white bg-[#9799a5]"
    }, {
      ratio: 0.17,
      className: "bg-[#c9c2c7]"
    }, {
      ratio: 0.14,
      className: "bg-[#c9c2c7]"
    }, {
      ratio: 0.134,
      className: "bg-[#e3dee4]"
    }, {
      ratio: 0.12,
      className: "border-2 border-[#a6a4a5] bg-[#bebcba] shadow-[0_0_24px_-12px_rgba(0,0,0,0.25)_inset]"
    }];
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== size) {
    t11 = t10.map(ring => {
      const ringSize = size * ring.ratio;
      return <div key={`${ring.ratio}-${ring.className}`} style={{
        width: ringSize,
        height: ringSize
      }} className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full ${ring.className}`} />;
    });
    $[21] = size;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t9) {
    t12 = <m.div animate={t9} className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">{t11}</m.div>;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t2 || $[28] !== t3 || $[29] !== t5 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8) {
    t13 = <m.div initial={t2} animate={t3} whileHover={t5} onClick={t6} style={t7} className="absolute left-1/2 z-10 flex origin-center cursor-pointer items-center justify-center overflow-hidden border-2 border-[#a89f9f] bg-neutral-400 shadow-[0_0_200px_-20px_rgba(0,0,0,0.6)] select-none">{t8}{t12}</m.div>;
    $[26] = t12;
    $[27] = t2;
    $[28] = t3;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  return t13;
}
function _temp(prev) {
  return prev === "expanded" ? "idle" : "expanded";
}
export default function SpinningCD(t0) {
  const $ = _c(62);
  const {
    artist: t1,
    title: t2,
    albumArt,
    size: explicitSize,
    inline: t3,
    previewUrl,
    onPreviewPlay
  } = t0;
  const artist = t1 === undefined ? "Unknown Artist" : t1;
  const title = t2 === undefined ? "Unknown Track" : t2;
  const inline = t3 === undefined ? false : t3;
  const [status, setStatus] = React.useState("idle");
  const [isAudioPlaying, setIsAudioPlaying] = React.useState(false);
  const audioRef = React.useRef(null);
  const fadeIntervalRef = React.useRef(null);
  const isExpanded = status === "expanded";
  const isInteracting = status !== "idle";
  const isMobile = useIsMobile();
  const size = explicitSize ?? (isMobile ? 280 : 400);
  let t4;
  let t5;
  if ($[0] !== previewUrl) {
    t4 = () => {
      if (previewUrl) {
        audioRef.current = new Audio(previewUrl);
        audioRef.current.volume = 0;
        audioRef.current.loop = true;
      }
      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
        if (fadeIntervalRef.current) {
          clearInterval(fadeIntervalRef.current);
        }
      };
    };
    t5 = [previewUrl];
    $[0] = previewUrl;
    $[1] = t4;
    $[2] = t5;
  } else {
    t4 = $[1];
    t5 = $[2];
  }
  React.useEffect(t4, t5);
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = fadeIn => {
      if (!audioRef.current) {
        return;
      }
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
      }
      const targetVolume = fadeIn ? AUDIO.maxVolume : 0;
      const step = fadeIn ? AUDIO.fadeStep : -AUDIO.fadeStep;
      const interval = AUDIO.fadeIntervalMs;
      if (fadeIn) {
        audioRef.current.play().catch(_temp2);
      }
      fadeIntervalRef.current = setInterval(() => {
        if (!audioRef.current) {
          return;
        }
        const newVolume = Math.max(0, Math.min(AUDIO.maxVolume, audioRef.current.volume + step));
        audioRef.current.volume = newVolume;
        if (newVolume === targetVolume) {
          if (fadeIntervalRef.current) {
            clearInterval(fadeIntervalRef.current);
          }
          if (!fadeIn && audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
        }
      }, interval);
      setIsAudioPlaying(() => fadeIn);
    };
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const fadeAudio = t6;
  let t7;
  if ($[4] !== isMobile || $[5] !== onPreviewPlay || $[6] !== previewUrl) {
    t7 = () => {
      if (!isMobile && previewUrl) {
        fadeAudio(true);
        onPreviewPlay?.();
      }
    };
    $[4] = isMobile;
    $[5] = onPreviewPlay;
    $[6] = previewUrl;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const handleMouseEnter = t7;
  let t8;
  if ($[8] !== isMobile || $[9] !== previewUrl) {
    t8 = () => {
      if (!isMobile && previewUrl) {
        fadeAudio(false);
      }
    };
    $[8] = isMobile;
    $[9] = previewUrl;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const handleMouseLeave = t8;
  let t9;
  if ($[11] !== isAudioPlaying || $[12] !== isMobile || $[13] !== onPreviewPlay || $[14] !== previewUrl) {
    t9 = () => {
      if (isMobile && previewUrl) {
        const willPlay = !isAudioPlaying;
        fadeAudio(willPlay);
        if (willPlay) {
          onPreviewPlay?.();
        }
      }
    };
    $[11] = isAudioPlaying;
    $[12] = isMobile;
    $[13] = onPreviewPlay;
    $[14] = previewUrl;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleTapToggle = t9;
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      fadeAudio(false);
    };
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const stopPreviewAudio = useEffectEvent(t10);
  let t11;
  if ($[17] !== isAudioPlaying || $[18] !== stopPreviewAudio) {
    t11 = () => {
      const handleEscape = function handleEscape(e) {
        if (e.key === "Escape") {
          setStatus("idle");
          if (isAudioPlaying) {
            stopPreviewAudio();
          }
        }
      };
      window.addEventListener("keydown", handleEscape);
      return () => window.removeEventListener("keydown", handleEscape);
    };
    $[17] = isAudioPlaying;
    $[18] = stopPreviewAudio;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== isAudioPlaying) {
    t12 = [setStatus, isAudioPlaying];
    $[20] = isAudioPlaying;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  React.useEffect(t11, t12);
  let t13;
  if ($[22] !== status) {
    t13 = {
      status,
      setStatus
    };
    $[22] = status;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const t14 = inline ? "relative" : "flex h-screen items-center justify-center";
  const t15 = size * 0.2;
  let t16;
  if ($[24] !== size || $[25] !== t15) {
    t16 = {
      width: size,
      height: size,
      borderRadius: t15
    };
    $[24] = size;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== handleTapToggle) {
    t17 = event => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleTapToggle();
      }
    };
    $[27] = handleTapToggle;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== albumArt || $[30] !== isAudioPlaying || $[31] !== size || $[32] !== title) {
    t18 = <CD albumArt={albumArt} title={title} size={size} isAudioPlaying={isAudioPlaying} />;
    $[29] = albumArt;
    $[30] = isAudioPlaying;
    $[31] = size;
    $[32] = title;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const t19 = isInteracting ? INFO.interactingScale : INFO.restingScale;
  const t20 = isInteracting ? `blur(${INFO.blurPx}px)` : "none";
  const t21 = isExpanded ? 0 : 1;
  let t22;
  if ($[34] !== t19 || $[35] !== t20 || $[36] !== t21) {
    t22 = {
      scale: t19,
      filter: t20,
      opacity: t21
    };
    $[34] = t19;
    $[35] = t20;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== title) {
    t23 = <p className="line-clamp-2 text-lg break-words text-[#272622] dark:text-zinc-200" title={title}>{title}</p>;
    $[38] = title;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== artist) {
    t24 = <p className="line-clamp-2 text-sm break-words text-[#a09c99] dark:text-zinc-400" title={artist}>{artist}</p>;
    $[40] = artist;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== t23 || $[43] !== t24) {
    t25 = <div className="flex w-full flex-col items-center gap-0.5 text-center font-mono">{t23}{t24}</div>;
    $[42] = t23;
    $[43] = t24;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== t22 || $[46] !== t25) {
    t26 = <m.div animate={t22} className="pointer-events-none absolute bottom-10 flex w-full flex-col items-center gap-0.5 px-6">{t25}</m.div>;
    $[45] = t22;
    $[46] = t25;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] !== handleMouseEnter || $[49] !== handleMouseLeave || $[50] !== handleTapToggle || $[51] !== t16 || $[52] !== t17 || $[53] !== t18 || $[54] !== t26) {
    t27 = <div style={t16} className="relative overflow-hidden border border-[#cacaca] bg-[#e3e3e3] font-medium tracking-tight shadow-[0_3px_3px_-1.5px_rgba(0,0,0,0.06)] dark:border-[#3a3a3a] dark:bg-[#2a2a2a]" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={handleTapToggle} onKeyDown={t17} role="button" tabIndex={0}>{t18}{t26}</div>;
    $[48] = handleMouseEnter;
    $[49] = handleMouseLeave;
    $[50] = handleTapToggle;
    $[51] = t16;
    $[52] = t17;
    $[53] = t18;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== t14 || $[57] !== t27) {
    t28 = <MotionConfig transition={SPRING_CD}><div className={t14}>{t27}</div></MotionConfig>;
    $[56] = t14;
    $[57] = t27;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== t13 || $[60] !== t28) {
    t29 = <Context.Provider value={t13}>{t28}</Context.Provider>;
    $[59] = t13;
    $[60] = t28;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  return t29;
}
function _temp2() {}